import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // Added for navigation
import { ChevronDown, Zap, Users, TrendingUp, Clock, MessageCircle, Bell, Calendar, Home, Key, Camera, BarChart3, ArrowRight, CheckCircle, Star, Mail, Phone, Send, Target, ChevronRight } from 'lucide-react'; 

// Assets - Ensure these paths match your project structure
import Image1 from "../../assets/realestate.webp";
import Image2 from "../../assets/realestate2.webp";

const Realestate = () => {

  // State to manage which accordion item is open
  const [openIndex, setOpenIndex] = useState(0);
  
  // Animation states
  const [counts, setCounts] = useState([0, 0, 0, 0]);
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef([]);

  // Real Estate specific content for the accordion
  const solutions = [
    {
      title: "Automated Property Alerts",
      description: "Instantly notify your lead database the moment a new property matches their search criteria. With a 98% open rate, SMS ensures your listings are seen immediately, giving you a competitive edge over email-only agencies."
    },
    {
      title: "Instant Lead Engagement",
      description: "Convert web inquiries into conversations. Automatically trigger a 'Thank You' SMS with a digital brochure or agent contact details the second a prospect submits a lead form on your website or portal."
    },
    {
      title: "Viewing & Site Visit Reminders",
      description: "Minimize no-shows by sending automated appointment reminders. Include Google Maps location links and agent names directly in the text to ensure a seamless experience for your potential buyers."
    },
    {
      title: "Rent & Payment Notifications",
      description: "Simplify property management by sending automated reminders for rent collection, maintenance schedules, or lease renewals. It's the most effective way to keep tenants informed and payments on time."
    },
    {
      title: "Virtual Tour & High-Res Gallery Distribution",
      description: "Send trackable links to 360° virtual tours or high-quality property images via SMS. Track engagement levels and follow up with the most interested leads who click through your content."
    }
  ];

  // Stats/Impact Section
  const impactStats = [
    { icon: <Zap className="w-8 h-8" />, value: "98%", label: "SMS Open Rate", suffix: "" },
    { icon: <Clock className="w-8 h-8" />, value: "5", label: "Avg Response Time", suffix: "min" },
    { icon: <Users className="w-8 h-8" />, value: "3x", label: "Higher Engagement", suffix: "" },
    { icon: <TrendingUp className="w-8 h-8" />, value: "47", label: "Conversion Increase", suffix: "%" }
  ];

  // Benefits Grid
  const benefits = [
    {
      icon: <Bell className="w-8 h-8 text-[#F46653]" />,
      title: "Instant Property Alerts",
      description: "Notify buyers immediately when matching properties hit the market with 98% open rate SMS"
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-[#F46653]" />,
      title: "Automated Follow-ups",
      description: "Never miss a lead with automated SMS sequences after property viewings"
    },
    {
      icon: <Calendar className="w-8 h-8 text-[#F46653]" />,
      title: "Smart Scheduling",
      description: "Reduce no-shows by 70% with automated viewing reminders and calendar integration"
    },
    {
      icon: <Home className="w-8 h-8 text-[#F46653]" />,
      title: "Property Management",
      description: "Streamline rent collection and maintenance updates with bulk SMS"
    },
    {
      icon: <Camera className="w-8 h-8 text-[#F46653]" />,
      title: "Virtual Tours",
      description: "Share 360° property tours via SMS with real-time engagement tracking"
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-[#F46653]" />,
      title: "Analytics Dashboard",
      description: "Track campaign performance and lead engagement in real-time"
    }
  ];

  // Testimonials/Process Steps
  const testimonials = [
    {
      quote: "Increased our property viewings by 40% using automated SMS alerts. Game-changer for our agency.",
      author: "Sarah Chen",
      role: "Luxury Real Estate Broker",
      rating: 5,
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop"
    },
    {
      quote: "The bulk SMS feature helped us reduce no-shows by 65%. Our agents can now focus on qualified leads.",
      author: "Michael Roberts",
      role: "Property Management Director",
      rating: 5,
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop"
    },
    {
      quote: "Virtual tour links via SMS increased engagement by 3x. Clients love the instant access to properties.",
      author: "Emily Parker",
      role: "Digital Marketing Head",
      rating: 5,
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop"
    }
  ];

  // Real Estate Workflow Tags
  const row1 = ["New Property Inquiry", "Virtual Tour Link", "Instant Listing Alert", "Price Drop Notification", "Agent Contact Details", "Viewing Scheduled"];
  const row2 = ["Site Visit Reminder", "KYC Document Link", "Rental Agreement Draft", "Payment Confirmation", "Maintenance Update", "Closing Deal Celebration"];

  // Intersection Observer for animations
  useEffect(() => {
    const observers = sectionRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(prev => ({ ...prev, [index]: true }));
            }
          });
        },
        { threshold: 0.3 }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  // Counter animation
  useEffect(() => {
    if (isVisible[0]) {
      const targets = [98, 5, 3, 47];
      const intervals = targets.map((target, index) => {
        return setInterval(() => {
          setCounts(prev => {
            const newCounts = [...prev];
            if (newCounts[index] < target) {
              newCounts[index] = Math.min(newCounts[index] + 1, target);
            }
            return newCounts;
          });
        }, 30);
      });

      return () => intervals.forEach(interval => clearInterval(interval));
    }
  }, [isVisible]);


  // Navigation hook
  const navigate = useNavigate();
  const handleSignupClick = () => {
    navigate("/login?mode=signup");
    };


  return (
    <div className="w-full overflow-hidden">
      {/* Premium Animation Styles */}
      <style>
        {`
            @keyframes scroll-left {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
            }
            @keyframes scroll-right {
                0% { transform: translateX(-50%); }
                100% { transform: translateX(0); }
            }
            .animate-scroll-left {
                display: flex;
                width: max-content;
                animation: scroll-left 40s linear infinite;
            }
            .animate-scroll-right {
                display: flex;
                width: max-content;
                animation: scroll-right 40s linear infinite;
            }
            .pause-on-hover:hover {
                animation-play-state: paused;
            }
        `}
      </style>

      {/* --- SECTION 1: HERO SECTION --- */}
      <section className="bg-[#EEF2FC] flex flex-col md:flex-row items-center justify-between px-6 py-10 md:px-12 lg:px-24 min-h-[600px]">
        <div className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-10 pt-12 md:pt-1 flex flex-col items-start -mt-16">
          <h1 className="text-4xl md:text-5xl font-medium text-slate-900 leading-tight tracking-tight mb-6">
            Close the Deal with Real Appeal — Instant SMS for Every Real Estate Reveal
          </h1>
          <p className="text-lg text-slate-700 mb-8 leading-relaxed">
            Reach buyers and renters instantly with smart property alerts and viewing reminders via bulk SMS—boost conversions and keep every lead engaged.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 px-6 py-3 text-white bg-slate-900 rounded-lg transition duration-300 shadow-md "
          >
            Request a Demo
            <ChevronRight className="w-5 h-5 animate-bounce" />
          </button>
        </div>
        <div className="w-full md:w-1/2 flex justify-center">
          <img
            src={Image1}
            alt="Real Estate SMS Solutions"
            className="relative w-auto max-w-lg object-contain -left-[10px] -top-[8px] h-[480px] rounded-xl"
          />
        </div>
      </section>
{/* --- SECTION 2: REAL ESTATE PROCESSES --- */}
<section className="bg-[#EEF2FC] py-16 lg:py-32 -mt-16">
  <div className="container mx-auto px-6 lg:px-12">
    <div className="text-center max-w-4xl mx-auto mb-12 lg:mb-24">
      <h2 className="text-3xl sm:text-4xl lg:text-6xl font-medium text-slate-900 leading-tight">
        How Eurofox can help <br className="hidden sm:block" />
        <span className="text-[#F46653]">Real estate processes</span>
      </h2>
    </div>
    
    {/* Mobile: Column layout | Desktop: Row layout */}
    <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
      
      {/* Image Container - Full width on mobile, 5/12 on desktop */}
      <div className="lg:w-5/12 w-full">
        <div className="relative rounded-2xl overflow-hidden shadow-lg w-full h-[300px] sm:h-[350px] lg:h-[530px]">
          <img
            src={Image2}
            alt="Eurofox Real Estate Process"
            className="w-full h-full object-cover object-center"
          />
        </div>
      </div>

      {/* Accordion Container - Full width on mobile, 7/12 on desktop */}
      <div className="lg:w-7/12 w-full space-y-3 lg:space-y-4">
        {solutions.map((item, index) => (
          <div key={index} className="bg-white border border-slate-200 rounded-xl lg:rounded-2xl shadow-sm transition-all duration-300 hover:shadow-md">
            <button
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              className="w-full flex items-center justify-between p-4 lg:p-6 text-left hover:bg-slate-50 transition-colors rounded-xl lg:rounded-2xl"
            >
              <span className="font-medium text-slate-800 text-base sm:text-lg lg:text-xl pr-3 lg:pr-4">
                {item.title}
              </span>
              <ChevronDown className={`w-4 h-4 lg:w-5 lg:h-5 text-slate-400 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} />
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-4 lg:px-6 pb-4 lg:pb-6 text-slate-600 text-sm sm:text-base lg:text-lg leading-relaxed border-t border-slate-50 pt-3 lg:pt-4">
                {item.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</section>
      {/* --- SECTION 3: BENEFITS GRID --- */}
      <section ref={el => sectionRefs.current[1] = el} className="py-20 bg-[#EEF2FC] relative -mt-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#F46653] font-semibold text-sm uppercase tracking-wider mb-4 block">Why Choose Eurofox</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Powerful Features for <span className="text-[#F46653] block mt-2">Real Estate Success</span>
            </h2>
            <p className="text-xl text-slate-600">Everything you need to streamline communication and close more deals</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`group bg-gradient-to-br from-white to-slate-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-[#F46653] rounded-2xl opacity-10 group-hover:opacity-20 transition-opacity duration-300 transform -rotate-6 group-hover:rotate-0"></div>
                  <div className="relative transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">{benefit.icon}</div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-[#F46653] transition-colors duration-300">{benefit.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-4">{benefit.description}</p>
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-[#F46653] group-hover:w-full transition-all duration-300 rounded-b-2xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

     {/* --- SECTION 4: PREMIUM CONVERSATIONAL WORKFLOWS --- */}
<section className="bg-[#EEF2FC] py-16 lg:py-32 border-t border-slate-100 -mt-16">
  <div className="container mx-auto px-6 lg:px-12 text-center">
    
    {/* Mobile Optimized Heading */}
    <h2 className="text-3xl sm:text-4xl lg:text-6xl font-medium text-slate-900 mb-4 lg:mb-6 tracking-tight">
      Faster engagement, faster sales —{" "}
      <span className="text-[#F46653]">SMS solutions</span>
      <br className="hidden sm:block lg:block" />
      <span className="text-[#F46653] text-2xl sm:text-3xl lg:text-6xl block sm:inline">
        for modern real estate
      </span>
    </h2>

    {/* Multi-Row Staggered Animation */}
    <div className="relative mt-12 lg:mt-20 mb-12 lg:mb-20 overflow-hidden">
      {/* Gradient Side Blurs - Adjusted for mobile */}
      <div className="absolute inset-y-0 left-0 w-20 lg:w-40 z-10 bg-gradient-to-r from-[#EEF2FC] to-transparent"></div>
      <div className="absolute inset-y-0 right-0 w-20 lg:w-40 z-10 bg-gradient-to-l from-[#EEF2FC] to-transparent"></div>

      {/* Row 1: Moving Left - Mobile optimized tags */}
      <div className="animate-scroll-left gap-4 lg:gap-8 mb-4 lg:mb-8">
        {[...row1, ...row1].map((tag, idx) => (
          <div
            key={idx}
            className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-xl lg:rounded-3xl text-slate-700 font-semibold text-sm sm:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4 cursor-default"
          >
            {tag}
          </div>
        ))}
      </div>

      {/* Row 2: Moving Right - Mobile optimized tags */}
      <div className="animate-scroll-right gap-4 lg:gap-8">
        {[...row2, ...row2].map((tag, idx) => (
          <div
            key={idx}
            className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-xl lg:rounded-3xl text-slate-700 font-semibold text-sm sm:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4 cursor-default"
          >
            {tag}
          </div>
        ))}
      </div>
    </div>

    {/* Mobile Optimized Description */}
    <p className="text-base sm:text-lg lg:text-2xl text-slate-600 font-normal max-w-4xl mx-auto leading-relaxed mb-10 lg:mb-16 px-2 lg:px-0">
      Empower your sales team and property managers with intelligent SMS workflows that capture leads, automate site visits, and accelerate the journey from listing to closing.
    </p>

    {/* Mobile Optimized Button */}
    <button 
      onClick={handleSignupClick}
      className="inline-flex items-center gap-2 lg:gap-3 px-6 py-3 lg:px-10 lg:py-5 text-white bg-slate-900 rounded-xl transition duration-300 shadow-xl hover:shadow-2xl text-base lg:text-xl font-semibold"
    >
      Start Your Learning Journey
      <Target className="w-5 h-5 lg:w-6 lg:h-6" />
    </button>
  </div>
</section>

      {/* --- SECTION 5: TESTIMONIALS & CTA --- */}
      <section ref={el => sectionRefs.current[2] = el} className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMzAgMzBhYXgzMCAzMCAwIDAgMSAwIDYwIDMwIDMwIDAgMCAxIDAtNjB6IiBmaWxsPSIjZmZmZmZmIi8+PC9zdmc+')] bg-repeat opacity-20 animate-pulse"></div>
        </div>
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">Trusted by Leading <span className="text-[#F46653] block mt-2">Real Estate Agencies</span></h2>
            <p className="text-xl text-slate-300">See how Eurofox is transforming real estate communication</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-2 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (<Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />))}
                </div>
                <p className="text-white/90 text-lg mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/50">
                    <img src={testimonial.image} alt={testimonial.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.author}</h4>
                    <p className="text-slate-300 text-sm">{testimonial.role}</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 text-6xl text-white/10 font-serif">"</div>
              </div>
            ))}
          </div>
          <div className={`max-w-4xl mx-auto bg-gradient-to-r from-[#F46653] to-[#F46653]/80 rounded-3xl p-12 text-center transform transition-all duration-1000 ${isVisible[2] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">Ready to Transform Your Real Estate Communication?</h3>
            <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">Join hundreds of real estate agencies using Eurofox to close more deals</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/login')}
                className="group bg-white text-slate-900 px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
              >
                Get Started Today <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-slate-900 transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" /> Schedule a Demo
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Realestate;